import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Form, Table } from "react-bootstrap";
import styled from "styled-components";

import AuthContext from "common/store/AuthContext";
import StatusContext from "common/store/StatusContext";

import { useWorkReport } from "../hooks/useWorkReport";
import { useImportTasks } from "../hooks/useImportTasks";
import { toFormattedDate } from "utils/DateTools";

import { WorkReportType } from "../types/WorkReportType";
import { RawTaskData } from "../types/ImportTasksData";

import FunctionButton from "common/components/atoms/FunctionButton";
import MyCheckbox from "common/components/atoms/MyCheckbox";
import PrimaryButton from "common/components/atoms/PrimaryButton";
import { TaskWithPeriod } from "../types/TaskWithPeriod";

type DataRead = {
  checked: boolean;
  rawData: RawTaskData;
  task?: TaskWithPeriod;
  error?: string;
};

type ImportTasks = {
  date: Date;
  value: DataRead[];
};

enum MODE {
  ADD,
  OVERWRITE,
}

export const ImportTasksPanel = () => {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const { selectedPark } = useContext(AuthContext);
  const { setErrorMessage, showToastMessage } = useContext(StatusContext);
  const { loadMetaData } = useWorkReport();
  const { readFile, convert, importTasks } = useImportTasks();

  const [tasksByDate, setTasksByDate] = useState<ImportTasks[]>();
  const [allChecked, setAllChecked] = useState(true);
  const [canStartImport, setCanStartImport] = useState(false);
  const [mode, setMode] = useState(MODE.ADD);

  //--------------------------------------------
  // READ FILE
  //--------------------------------------------
  /**
   * Input file selected
   */
  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!selectedPark) return;
    setErrorMessage("");

    const { succeeded, data, msg } = await loadMetaData(
      selectedPark.parkId,
      WorkReportType.ParkWork
    );
    if (!succeeded) {
      setErrorMessage(`作業のメタ情報取得に失敗しました。${msg}`);
      return;
    }
    const meta = data;
    if (!meta) return; // ありえない

    const files = e.target.files;
    if (files && files[0]) {
      try {
        const { parkId, tasksByDate } = await readFile(files[0]);
        if (parkId !== selectedPark?.parkId) throw new Error("別の公園の計画です");

        const newTasksByDate = tasksByDate.map((x) => {
          return {
            date: x.date,
            value: x.tasks.map((y) => {
              try {
                const converted = convert(y, meta);
                return { rawData: y, task: converted, checked: true } as DataRead;
              } catch (err: any) {
                return { rawData: y, error: err.message } as DataRead;
              }
            }),
          } as ImportTasks;
        });

        setTasksByDate(newTasksByDate);
      } catch (err: unknown) {
        setErrorMessage((err as Error).message);
      }
    }
  };

  const clearSelected = () => {
    if (fileInput.current) fileInput.current.value = "";
    setTasksByDate(undefined);
    setAllChecked(true);
  };

  //--------------------------------------------
  // CHECK ROW
  //--------------------------------------------
  const onCheckListChanged = () => {
    if (!tasksByDate) return;
    setAllChecked(tasksByDate.every((x) => x.value.forEach((y) => y.checked)));
    updateCanStartImport();
  };

  const onAllCheckChanged = (checked: boolean) => {
    if (tasksByDate) {
      tasksByDate.forEach((x) => x.value.forEach((y) => (y.checked = checked)));
    }
    setAllChecked(checked);
    updateCanStartImport();
  };

  const updateCanStartImport = () => {
    if (!tasksByDate) return;
    const rowsToImport = tasksByDate.map((x) => x.value.filter((y) => y.checked)).flat();

    setCanStartImport(rowsToImport.length > 0 && rowsToImport.every((x) => !x.error));
  };
  //--------------------------------------------
  // EXECUTE IMPORT
  //--------------------------------------------
  const onStartImportClick = async () => {
    if (!tasksByDate || !selectedPark) return;

    const {
      succeeded,
      data: meta,
      msg,
    } = await loadMetaData(selectedPark.parkId, WorkReportType.ParkWork);
    if (!succeeded || !meta) {
      setErrorMessage(`作業のメタ情報取得に失敗しました。${msg}`);
      return;
    }

    let failed = false;
    // 日付ごとに保存をする
    for (const x of tasksByDate) {
      const tasksToSave = x.value.filter((y) => y.checked).map((task) => task.task);
      if (tasksToSave.length === 0) continue;

      try {
        await importTasks(
          selectedPark.parkId,
          x.date,
          tasksToSave,
          meta,
          mode === MODE.OVERWRITE,
          "作業のインポート"
        );
      } catch (err) {
        failed = true;
        setErrorMessage((err as Error).message);
      }
    }
    if (!failed) showToastMessage("作業計画取込", "取込が完了しました");
  };

  useEffect(() => {
    updateCanStartImport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksByDate]);

  return (
    <SRootDiv>
      <STopDiv>
        <STitle>作業計画ファイル</STitle>
        <SFileInput
          type="file"
          ref={fileInput}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={onFileSelect}
        />
        <FunctionButton buttonType="edit" size="md" tooltip="クリアー" onClick={clearSelected}>
          <i className="fas fa-eraser" />
        </FunctionButton>
      </STopDiv>
      {tasksByDate && (
        <SContentsDiv>
          <SDataDiv>
            <Table bordered hover responsive>
              <thead className="text-center">
                <tr>
                  <th style={{ width: "50px" }}>
                    <Form.Check
                      checked={allChecked}
                      onChange={(e) => onAllCheckChanged(e.currentTarget.checked)}
                    />
                  </th>
                  <Sth2>日付</Sth2>
                  <Sth2>時間区分</Sth2>
                  <Sth2>作業種別</Sth2>
                  <Sth1>計画数量</Sth1>
                  <Sth3>作業内容</Sth3>
                  <Sth2>事業者区分</Sth2>
                  <Sth2>事業者名称</Sth2>
                  <Sth2>地区名称</Sth2>
                  <Sth2>整合性チェック</Sth2>
                </tr>
              </thead>
              <tbody>
                {tasksByDate.map((dataByDate, index) => (
                  <Fragment key={`date_${dataByDate.date}_${index}`}>
                    {dataByDate.value.map((data, index2) => (
                      <tr key={`date_${dataByDate.date}_${index2}`}>
                        <TdCenter>
                          <MyCheckbox
                            initialValue={data.checked}
                            changed={(val) => {
                              data.checked = val;
                              onCheckListChanged();
                            }}
                          />
                        </TdCenter>
                        <td>{toFormattedDate(dataByDate.date)}</td>
                        <td>{data.rawData.period}</td>
                        <td>{data.rawData.category}</td>
                        <td>{data.rawData.plannedAmount}</td>
                        <td>{data.rawData.taskContent}</td>
                        <td>{data.rawData.contractType}</td>
                        <td>{data.rawData.contractorName}</td>
                        <td>{data.rawData.areas}</td>
                        <td>
                          {data.error ? (
                            <span style={{ color: "red" }}>{data.error}</span>
                          ) : (
                            <span>OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </SDataDiv>
          <SBottomDiv>
            <SRadioDiv>
              <Form.Label as="h6"> 重複時の動作 </Form.Label>
              <div>
                <Form.Check
                  label="追加する"
                  type="radio"
                  id="radio_add"
                  checked={mode === MODE.ADD}
                  onChange={() => setMode(MODE.ADD)}
                />
                <Form.Check
                  label="上書きする"
                  type="radio"
                  id="radio_update"
                  checked={mode === MODE.OVERWRITE}
                  onChange={() => setMode(MODE.OVERWRITE)}
                />
              </div>
            </SRadioDiv>
            <PrimaryButton
              title="取込開始"
              onClick={onStartImportClick}
              disabled={!canStartImport}
            />
          </SBottomDiv>
        </SContentsDiv>
      )}
    </SRootDiv>
  );
};

const SRootDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  padding: 0 0rem;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const STopDiv = styled.div`
  justify-self: stretch;
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SFileInput = styled(Form.Control)`
  max-width: 400px;
  @media screen and (max-width: 768px) {
    font-size: smaller;
  }
`;

const SContentsDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SDataDiv = styled.div`
  max-height: 400px;
  overflow: auto;
  width: 100%;
`;

const STitle = styled.span`
  margin: 0 1rem 0 0;
  min-width: 80px;
`;

const TdCenter = styled.td`
  vertical-align: middle;
  text-align: center;
`;

const SBottomDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.2rem;
  margin-top: 1%;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
`;

const SRadioDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5rem;
  div {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }
`;

const Sth1 = styled.th`
  min-width: 60px;
`;

const Sth2 = styled.th`
  min-width: 100px;
`;

const Sth3 = styled.th`
  min-width: 120px;
`;
